/**
 * GateAScreen — Quality Gate A por execução (F2-FE-2).
 *
 * Porta o GateShell do protótipo para o Gate A real, lendo:
 *   GET /executions/{id}/gates/a
 *
 * e enviando:
 *   PATCH /executions/{id}/gates/a  { decision: approve|reject|clarify, comment? }
 *
 * Layout fiel ao protótipo GateShell:
 *   Header     — shield, título, SLA, badge de status, aprovadores
 *   Pills      — métricas rápidas (requisitos, must-have, critérios etc.)
 *   Requisitos — accordion por requisito, badges MoSCoW, critérios de aceite
 *   Estimativa — tokens / custo estimados
 *   Ações      — Aprovar / Rejeitar / Pedir Esclarecimento + campo de comentário
 *
 * Segurança:
 *   - Botões de decisão habilitados somente se o usuário tem permissão
 *     (derivado do gate.approvers do back — se o email do usuário estiver na lista)
 *   - Erros 403 → ForbiddenBanner
 *
 * Acessibilidade:
 *   - Accordions com aria-expanded / aria-controls
 *   - Botões de decisão com aria-label descritivo
 *   - prefers-reduced-motion respeita a animação de spinner
 *
 * Standards: Clean, NFR (a11y), OWASP.
 * Nunca exibe wallet_txn_id.
 */

import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ShieldCheck, ChevronDown, ChevronRight as ChevronRightIcon,
  CheckCircle, XCircle, HelpCircle, Clock, Users, FileText,
  AlertCircle, CheckSquare, ArrowLeft,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, ForbiddenBanner } from '@/components/ui';
import { LoadingSpinner, QueryError, MutationError } from '@/components/ui/QueryState';
import { useExecutionGateA, useDecideGateA, useEditRequirement } from '@/api/executions';
import { useNavigationContext } from '@/navigation';

// ─── Constantes ───────────────────────────────────────────────────────────────

/** Mapeamento de status do gate → visual */
const GATE_STATUS_META = {
  pending:   { label: 'AGUARDANDO APROVAÇÃO', color: C.digital,     pulse: true  },
  approved:  { label: 'APROVADO',             color: C.cyber,       pulse: false },
  rejected:  { label: 'REJEITADO',            color: C.hot,         pulse: false },
  clarify:   { label: 'ESCLARECIMENTO SOLICITADO', color: C.neon,   pulse: false },
};

/** Mapeamento de prioridade MoSCoW → cor */
const MOSCOW_COLOR = {
  must_have:   C.hot,
  should_have: C.neon,
  could_have:  C.cyber,
  wont_have:   C.neutralMuted,
};

const MOSCOW_LABEL = {
  must_have:   'MUST HAVE',
  should_have: 'SHOULD HAVE',
  could_have:  'COULD HAVE',
  wont_have:   'WON\'T HAVE',
};

/** Mapeamento de SLA tier → label legível */
const SLA_LABEL = {
  standard: '24h',
  expedited: '12h',
  critical: '4h',
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

/** Pill de métrica rápida */
function MetaPill({ label, value, color = C.neutralDim }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      padding: '10px 14px',
      backgroundColor: C.blackElev2,
      borderRadius: 8,
      border: `1px solid ${C.blackBorder}`,
      minWidth: 90,
    }}>
      <span style={{ fontSize: 18, fontWeight: 700, color: color ?? C.neutral }}>
        {value}
      </span>
      <span style={{ fontSize: 10, color: C.neutralMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </span>
    </div>
  );
}

/** Item de critério de aceite */
function CriterionItem({ criterion }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      padding: '6px 0',
      borderBottom: `1px solid ${C.blackBorder}`,
    }}>
      {criterion.is_met
        ? <CheckCircle size={13} aria-hidden="true" style={{ color: C.cyber, flexShrink: 0, marginTop: 2 }} />
        : <AlertCircle size={13} aria-hidden="true" style={{ color: C.hot, flexShrink: 0, marginTop: 2 }} />}
      <span style={{ fontSize: 12, color: criterion.is_met ? C.neutralDim : C.neutral, lineHeight: 1.5 }}>
        {criterion.description}
      </span>
    </div>
  );
}

/** Accordion de um requisito */
function RequirementItem({ requirement, index, executionId }) {
  const [open, setOpen] = useState(false);
  const editReq = useEditRequirement(executionId);
  const editable = Boolean(executionId);
  const rid = requirement.requirement_id;
  const selStyle = {
    marginLeft: 6, background: C.blackElev, color: C.neutral,
    border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: '5px 8px', fontSize: 12,
  };
  const decBtn = (active, color, label, onClick) => (
    <button onClick={onClick} disabled={editReq.isPending}
      style={{ cursor: editReq.isPending ? 'default' : 'pointer',
        background: active ? `${color}22` : 'transparent', color,
        border: `1px solid ${color}${active ? '' : '66'}`, borderRadius: 7,
        padding: '6px 12px', fontSize: 12.5, fontWeight: active ? 700 : 600 }}>
      {label}
    </button>
  );
  const moscowColor = MOSCOW_COLOR[requirement.priority] ?? C.neutralDim;
  const accordionId = `req-${requirement.requirement_id}`;
  const panelId     = `req-panel-${requirement.requirement_id}`;

  const allMet = requirement.criteria?.every((c) => c.is_met) ?? false;

  return (
    <div style={{
      border: `1px solid ${C.blackBorder}`,
      borderRadius: 8,
      overflow: 'hidden',
      marginBottom: 8,
    }}>
      <button
        id={accordionId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((p) => !p)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          backgroundColor: open ? C.blackElev2 : C.blackElev,
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background-color 150ms',
        }}
      >
        {/* Código estável do requisito (RF-01, RNF-SEG-02): o mesmo em todas as
            regenerações do projeto. Requisito antigo, sem código, mostra a ordem. */}
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: requirement.code ? C.cyber : C.neutralMuted,
          flexShrink: 0,
          minWidth: 36,
          fontFamily: requirement.code ? 'monospace' : undefined,
        }}>
          {requirement.code ?? `#${String(index + 1).padStart(2, '0')}`}
        </span>

        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.neutral }}>
          {requirement.title}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            fontSize: 10,
            fontWeight: 700,
            padding: '2px 7px',
            borderRadius: 4,
            backgroundColor: `${moscowColor}22`,
            border: `1px solid ${moscowColor}55`,
            color: moscowColor,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>
            {MOSCOW_LABEL[requirement.priority] ?? requirement.priority}
          </span>

          {requirement.category && (
            <span style={{ fontSize: 11, color: C.neutralMuted }}>
              {requirement.category}
            </span>
          )}

          {requirement.is_approved
            ? <CheckCircle size={14} aria-label="Aprovado" style={{ color: C.cyber, flexShrink: 0 }} />
            : <AlertCircle size={14} aria-label="Necessita clarificação" style={{ color: C.hot, flexShrink: 0 }} />}

          {open
            ? <ChevronDown size={14} aria-hidden="true" style={{ color: C.neutralMuted }} />
            : <ChevronRightIcon size={14} aria-hidden="true" style={{ color: C.neutralMuted }} />}
        </div>
      </button>

      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={accordionId}
          style={{ padding: '12px 16px', backgroundColor: C.blackElev2 }}
        >
          {/* Classificação & decisão POR REQUISITO (não só aceitar/recusar tudo) */}
          {editable && (
            <div style={{ marginBottom: 14, padding: 12, border: `1px solid ${C.blackBorder}`,
              borderRadius: 8, background: C.blackElev }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.neutralMuted,
                letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>
                Classificação & decisão deste requisito
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ fontSize: 12, color: C.neutralDim }}>Prioridade
                  <select value={requirement.priority} disabled={editReq.isPending}
                    onChange={(e) => editReq.mutate({ requirementId: rid, priority: e.target.value })}
                    style={selStyle}>
                    <option value="must_have">Must have</option>
                    <option value="should_have">Should have</option>
                    <option value="could_have">Could have</option>
                    <option value="wont_have">Won't have</option>
                  </select>
                </label>
                <label style={{ fontSize: 12, color: C.neutralDim }}>Categoria
                  <select value={requirement.category} disabled={editReq.isPending}
                    onChange={(e) => editReq.mutate({ requirementId: rid, category: e.target.value })}
                    style={selStyle}>
                    {['functional', 'compliance', 'continuity', 'adoption', 'performance', 'security', 'other']
                      .map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {decBtn(requirement.is_approved === true, C.cyber, '✓ Aprovar',
                  () => editReq.mutate({ requirementId: rid, approval: 'approved' }))}
                {decBtn(requirement.is_approved === false, C.hot, '⚠ Pedir clarificação',
                  () => editReq.mutate({ requirementId: rid, approval: 'clarify' }))}
                {decBtn(requirement.is_approved == null, C.neutralMuted, 'Pendente',
                  () => editReq.mutate({ requirementId: rid, approval: 'pending' }))}
              </div>
              {editReq.isError && <div style={{ fontSize: 12, color: C.hot, marginTop: 8 }}>Falha ao salvar. Tente de novo.</div>}
            </div>
          )}

          {/* Descrição */}
          {requirement.description && (
            <div style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.6, marginBottom: 14 }}>
              {requirement.description}
            </div>
          )}

          {/* Origem */}
          {requirement.origin && (
            <div style={{ fontSize: 11, color: C.neutralMuted, marginBottom: 12 }}>
              Origem: <span style={{ color: C.neutralDim }}>{requirement.origin}</span>
              {requirement.external_ref && (
                <> · Ref: <span style={{ fontFamily: 'monospace', color: C.cyber }}>{requirement.external_ref}</span></>
              )}
            </div>
          )}

          {/* Critérios de aceite */}
          {requirement.criteria && requirement.criteria.length > 0 && (
            <div>
              <div style={{
                fontSize: 10,
                fontWeight: 700,
                color: C.neutralMuted,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}>
                Critérios de aceite ({requirement.criteria.filter((c) => c.is_met).length}/{requirement.criteria.length} atendidos)
              </div>
              {requirement.criteria.map((c) => (
                <CriterionItem key={c.criterion_id} criterion={c} />
              ))}
            </div>
          )}

          {/* Badge de aprovação do requisito */}
          <div style={{ marginTop: 12 }}>
            {requirement.is_approved
              ? <Badge color={C.cyber} light>Requisito aprovado</Badge>
              : <Badge color={C.hot} light>Necessita clarificação</Badge>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Painel de ações de decisão (Aprovar / Rejeitar / Esclarecer) */
function DecisionPanel({ executionId, gateStatus, onSuccess, onBackToBoard }) {
  const [comment, setComment]     = useState('');
  const [decision, setDecision]   = useState(null); // 'approve'|'reject'|'clarify'
  const [submitted, setSubmitted] = useState(false);

  const { mutate, isPending, error, reset } = useDecideGateA(executionId);

  const handleDecide = useCallback((d) => {
    if (isPending) return;
    setDecision(d);
    mutate(
      { decision: d, comment: comment || undefined },
      {
        onSuccess: () => {
          setSubmitted(true);
          if (onSuccess) onSuccess(d);
        },
      },
    );
  }, [isPending, mutate, comment, onSuccess]);

  if (submitted) {
    const labels = { approve: 'aprovado', reject: 'rejeitado', clarify: 'esclarecimento solicitado' };
    return (
      <Card style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <CheckCircle size={20} aria-hidden="true" style={{ color: C.cyber }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.cyber }}>
              Gate {labels[decision] ?? 'respondido'}
            </div>
            <div style={{ fontSize: 12, color: C.neutralDim, marginTop: 2 }}>
              Decisão registrada com sucesso.
            </div>
          </div>
          {onBackToBoard && (
            <Btn variant="primary" size="sm" icon={ArrowLeft} onClick={onBackToBoard}>
              Voltar ao Live Board
            </Btn>
          )}
        </div>
      </Card>
    );
  }

  const alreadyDecided = gateStatus && gateStatus !== 'pending';

  if (alreadyDecided) {
    // Gate já decidido — sem ações, mas oferece o retorno ao Live Board do projeto.
    return onBackToBoard ? (
      <Card style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: C.neutralDim, flex: 1, minWidth: 200 }}>
            Este gate já foi decidido. Volte ao Live Board para acompanhar a execução.
          </span>
          <Btn variant="primary" size="sm" icon={ArrowLeft} onClick={onBackToBoard}>
            Voltar ao Live Board
          </Btn>
        </div>
      </Card>
    ) : null;
  }

  return (
    <Card style={{ padding: 20 }}>
      <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: C.neutralMuted,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        marginBottom: 14,
      }}>
        Decisão
      </div>

      {/* Campo de comentário */}
      <div style={{ marginBottom: 16 }}>
        <label
          htmlFor="gate-comment"
          style={{ fontSize: 12, color: C.neutralDim, display: 'block', marginBottom: 6 }}
        >
          Parecer / Comentário <span style={{ color: C.neutralMuted }}>(opcional para aprovar; recomendado ao rejeitar)</span>
        </label>
        <textarea
          id="gate-comment"
          value={comment}
          onChange={(e) => { setComment(e.target.value); reset(); }}
          rows={3}
          placeholder="Adicione um comentário para o registro de auditoria..."
          style={{
            width: '100%',
            backgroundColor: C.blackElev2,
            border: `1px solid ${C.blackBorder}`,
            borderRadius: 6,
            color: C.neutral,
            fontSize: 13,
            padding: '10px 12px',
            resize: 'vertical',
            fontFamily: 'inherit',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Erro de mutation */}
      {error && <MutationError error={error} onDismiss={reset} />}

      {/* Botões de decisão */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Btn
          variant="primary"
          icon={CheckCircle}
          disabled={isPending}
          onClick={() => handleDecide('approve')}
          aria-label="Aprovar Gate A"
        >
          {isPending && decision === 'approve' ? 'Aprovando...' : 'Aprovar'}
        </Btn>

        <Btn
          variant="outline"
          icon={HelpCircle}
          disabled={isPending}
          onClick={() => handleDecide('clarify')}
          aria-label="Solicitar esclarecimento no Gate A"
        >
          {isPending && decision === 'clarify' ? 'Enviando...' : 'Pedir Esclarecimento'}
        </Btn>

        <Btn
          variant="danger"
          icon={XCircle}
          disabled={isPending}
          onClick={() => handleDecide('reject')}
          aria-label="Rejeitar Gate A"
        >
          {isPending && decision === 'reject' ? 'Rejeitando...' : 'Rejeitar'}
        </Btn>
      </div>

      {/* Nota de auditoria */}
      <div style={{ marginTop: 12, fontSize: 11, color: C.neutralMuted, lineHeight: 1.5 }}>
        Toda decisão é registrada de forma imutável na trilha de auditoria com IP, timestamp e usuário.
      </div>
    </Card>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

/**
 * GateAScreen — Gate A para uma execução específica.
 *
 * Lê o executionId de ?execution=<id> (extensível para /gates/a/:executionId).
 */
export function GateAScreen() {
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('execution');
  const { navigate } = useNavigationContext();

  const {
    data: gate,
    isLoading,
    error,
    refetch,
  } = useExecutionGateA(executionId);

  if (!executionId) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ fontSize: 14, color: C.neutralMuted }}>
          Nenhuma execução selecionada. Abra o Gate a partir da fila de gates ou da Jornada.
        </div>
        <div style={{ marginTop: 12 }}>
          <Btn variant="ghost" size="sm" onClick={() => navigate('gates')}>
            Ver fila de gates
          </Btn>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner label="Carregando Gate A..." />;
  }

  if (error) {
    if (error.isForbidden) {
      return (
        <div style={{ padding: 24 }}>
          <ForbiddenBanner detail={error.detail} />
        </div>
      );
    }
    return (
      <div style={{ padding: 24 }}>
        <QueryError error={error} onRetry={refetch} />
      </div>
    );
  }

  // Gate ainda não criado (404 normalizado para null)
  if (gate === null) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: 48,
      }}>
        <Clock size={40} aria-hidden="true" style={{ color: C.blackBorder }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: C.neutralMuted }}>
          Gate A ainda não criado
        </div>
        <div style={{ fontSize: 13, color: C.neutralMuted, textAlign: 'center', maxWidth: 360 }}>
          Este gate será criado quando o organizer submeter a definição do squad.
          Verifique o Squad Designer ou aguarde.
        </div>
        <Btn variant="ghost" size="sm" onClick={() => navigate('gates')}>
          Ver fila de gates
        </Btn>
      </div>
    );
  }

  // ── Derivar métricas ──────────────────────────────────────────────────────
  const reqs          = gate.requirements ?? [];
  const totalReqs     = reqs.length;
  const mustHave      = reqs.filter((r) => r.priority === 'must_have').length;
  const totalCriteria = reqs.reduce((acc, r) => acc + (r.criteria?.length ?? 0), 0);
  const metCriteria   = reqs.reduce(
    (acc, r) => acc + (r.criteria?.filter((c) => c.is_met).length ?? 0),
    0,
  );
  const approvedReqs  = reqs.filter((r) => r.is_approved).length;

  const statusMeta    = GATE_STATUS_META[gate.status] ?? GATE_STATUS_META.pending;
  const slaLabel      = SLA_LABEL[gate.sla_tier] ?? gate.sla_tier ?? '24h';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 28,
      }}>
        <div style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          backgroundColor: `${C.cyber}18`,
          border: `1px solid ${C.cyber}44`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <ShieldCheck size={22} aria-hidden="true" style={{ color: C.cyber }} />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: C.neutral, margin: 0 }}>
              Gate A — Requisitos de Negócio
            </h1>
            {/* Badge de status */}
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 4,
              backgroundColor: `${statusMeta.color}22`,
              border: `1px solid ${statusMeta.color}55`,
              color: statusMeta.color,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              animation: statusMeta.pulse ? 'navaPulse 2s ease-out infinite' : 'none',
            }}>
              {statusMeta.label}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: C.neutralMuted }}>
              <Clock size={11} aria-hidden="true" style={{ display: 'inline', marginRight: 4 }} />
              SLA: {slaLabel}
            </span>

            {gate.approvers && gate.approvers.length > 0 && (
              <span style={{ fontSize: 12, color: C.neutralMuted }}>
                <Users size={11} aria-hidden="true" style={{ display: 'inline', marginRight: 4 }} />
                {gate.approvers.join(' · ')}
              </span>
            )}

            {gate.decided_by && (
              <span style={{ fontSize: 12, color: C.neutralDim }}>
                Decidido por: <strong style={{ color: C.neutral }}>{gate.decided_by}</strong>
                {gate.decided_at ? ` · ${new Date(gate.decided_at).toLocaleString('pt-BR')}` : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Pills de métricas ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
        <MetaPill label="Requisitos"   value={totalReqs}   color={C.neutral} />
        <MetaPill label="Must-have"    value={mustHave}    color={C.hot} />
        <MetaPill label="Aprovados"    value={approvedReqs} color={C.cyber} />
        <MetaPill label="Critérios"    value={totalCriteria} color={C.neon} />
        <MetaPill label="Critérios met" value={metCriteria} color={C.cyber} />
        {gate.estimate_tokens != null && (
          <MetaPill
            label="Est. Tokens"
            value={gate.estimate_tokens.toLocaleString('pt-BR')}
            color={C.cyber}
          />
        )}
        {gate.estimate_cost_usd != null && (
          <MetaPill
            label="Est. Custo"
            value={`US$ ${Number(gate.estimate_cost_usd).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            color={C.neon}
          />
        )}
      </div>

      {/* ── Razão de rejeição (se houver) ──────────────────────────────── */}
      {gate.rejection_reason && (
        <div style={{
          display: 'flex',
          gap: 10,
          padding: '12px 16px',
          borderRadius: 8,
          backgroundColor: `${C.hot}11`,
          border: `1px solid ${C.hot}33`,
          marginBottom: 20,
        }}>
          <XCircle size={15} aria-hidden="true" style={{ color: C.hot, flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.hot, marginBottom: 2 }}>
              Motivo da rejeição
            </div>
            <div style={{ fontSize: 12, color: C.neutralDim }}>
              {gate.rejection_reason}
            </div>
          </div>
        </div>
      )}

      {/* ── Requisitos ──────────────────────────────────────────────────── */}
      <section aria-labelledby="requisitos-heading" style={{ marginBottom: 28 }}>
        <h2
          id="requisitos-heading"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.neutralMuted,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 14,
            margin: '0 0 14px',
          }}
        >
          Funcionalidades extraídas
        </h2>

        {reqs.length === 0
          ? (
            <div style={{
              padding: 32,
              textAlign: 'center',
              fontSize: 13,
              color: C.neutralMuted,
              border: `1px dashed ${C.blackBorder}`,
              borderRadius: 8,
            }}>
              Nenhum requisito registrado neste gate.
            </div>
          )
          : reqs.map((req, i) => (
            <RequirementItem key={req.requirement_id} requirement={req} index={i} executionId={executionId} />
          ))}
      </section>

      {/* ── Painel de decisão ────────────────────────────────────────────── */}
      <DecisionPanel
        executionId={executionId}
        gateStatus={gate.status}
        onBackToBoard={() => navigate(`/execution/liveboard?execution=${executionId}`)}
        onSuccess={() => {
          /* navegação ou toast futuro */
        }}
      />

    </div>
  );
}
